import type { DirectoryEntry } from "@givehere/contracts";

/**
 * Whether there is something **on** at this organization, as opposed to somewhere to send a gift.
 *
 * The server's answer, read and never re-derived (GH-790). It used to be worked out here from
 * `liveEventId` and `liveEventIsGeneralFund`, and that was two defects in one line: an unclaimed
 * listing holding any event row read as live, and so did a fundraiser published for next month.
 * The repository now decides it once — claimed and able to take a gift, a real event rather than
 * the general fund, started, not ended — and ships it as `isHappening` on every row. The iPhone
 * reads the same field in `DirectoryEntry.isHappening`, so the two surfaces cannot disagree.
 *
 * Kept as a function rather than inlined so every caller in this app asks the question the same
 * way, and so a reader looking for "how does the web decide" lands on the sentence above.
 */
export function isHappening(entry: DirectoryEntry): boolean {
  return entry.isHappening;
}

/**
 * One directory answer as the screen's two lists: what is on, then everyone else.
 *
 * Order inside each half is left exactly as it arrived — the server has already sorted by distance
 * or alphabetically, and re-sorting here would be a second opinion the footnote does not promise.
 *
 * A search is never split (`splitting: false`): somebody who typed a name is looking for that
 * organization, and filing it under a second heading because nothing is running tonight would hide
 * the row they asked for.
 */
export function splitDirectory(
  entries: readonly DirectoryEntry[],
  splitting: boolean,
): { readonly happening: DirectoryEntry[]; readonly organizations: DirectoryEntry[] } {
  if (!splitting) return { happening: [...entries], organizations: [] };
  return {
    happening: entries.filter(isHappening),
    organizations: entries.filter((entry) => !isHappening(entry)),
  };
}
